import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountsAPI, alpacaAccountsAPI, upstoxAccountsAPI, fyersAccountsAPI, usersAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, ToggleLeft, ToggleRight, Key, ExternalLink, Package, CheckCircle, Eye } from 'lucide-react';

const Accounts: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'kite' | 'alpaca' | 'upstox' | 'fyers'>('kite');
  const [showForm, setShowForm] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [requestToken, setRequestToken] = useState('');
  const [selectedAccountForHoldings, setSelectedAccountForHoldings] = useState<any>(null);
  const [showHoldings, setShowHoldings] = useState(false);
  const [formData, setFormData] = useState({
    account_name: '',
    kite_user_id: '',
    api_key: '',
    api_secret: '',
    user_id: '',
  });

  // Listen for messages from popup window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'KITE_AUTH_SUCCESS') {
        setRequestToken(event.data.request_token);
        toast.success('Request token received! Click Authenticate to complete.');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const { data: accounts, isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsAPI.list().then((res) => res.data)
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersAPI.list().then((res) => res.data)
  });

  const { data: holdings, isLoading: holdingsLoading, refetch: refetchHoldings } = useQuery({
    queryKey: ['holdings', selectedAccountForHoldings?.id],
    queryFn: () => accountsAPI.getSyncedHoldings(selectedAccountForHoldings.id).then((res) => res.data),
    enabled: !!selectedAccountForHoldings,
  });

  const createMutation = useMutation({
    mutationFn: accountsAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      toast.success('Account created successfully');
      setShowForm(false);
      setFormData({ account_name: '', kite_user_id: '', api_key: '', api_secret: '', user_id: '' });
    },
    onError: () => toast.error('Failed to create account'),
  });

  const toggleMutation = useMutation({
    mutationFn: accountsAPI.toggle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      toast.success('Account status updated');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: accountsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      toast.success('Account deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    },
  });

  const authenticateMutation = useMutation({
    mutationFn: ({ id, token }: { id: number; token: string }) =>
      accountsAPI.authenticate(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      toast.success('Account authenticated successfully!');
      setShowAuthModal(false);
      setRequestToken('');
      setSelectedAccount(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    },
  });

  const syncHoldingsMutation = useMutation({
    mutationFn: (accountId: number) => accountsAPI.syncHoldings(accountId),
    onSuccess: () => {
      toast.success('Holdings synced successfully');
      refetchHoldings();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to sync holdings');
    },
  });

  // ─── Alpaca state & queries ──────────────────────────────────────────────────
  const [showAlpacaForm, setShowAlpacaForm] = useState(false);
  const [showAlpacaPositions, setShowAlpacaPositions] = useState(false);
  const [selectedAlpacaAccount, setSelectedAlpacaAccount] = useState<any>(null);
  const [alpacaFormData, setAlpacaFormData] = useState({
    account_name: '',
    api_key: '',
    api_secret: '',
    is_paper: true,
    user_id: '',
  });

  const { data: alpacaAccounts, isLoading: alpacaLoading } = useQuery({
    queryKey: ['alpacaAccounts'],
    queryFn: () => alpacaAccountsAPI.list().then((res) => res.data),
  });

  const { data: alpacaPositions, isLoading: positionsLoading } = useQuery({
    queryKey: ['alpacaPositions', selectedAlpacaAccount?.id],
    queryFn: () => alpacaAccountsAPI.getPositions(selectedAlpacaAccount.id).then((res) => res.data),
    enabled: !!selectedAlpacaAccount && showAlpacaPositions,
  });

  const createAlpacaMutation = useMutation({
    mutationFn: alpacaAccountsAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alpacaAccounts'] });
      toast.success('Alpaca account created');
      setShowAlpacaForm(false);
      setAlpacaFormData({ account_name: '', api_key: '', api_secret: '', is_paper: true, user_id: '' });
    },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to create account'),
  });

  const verifyAlpacaMutation = useMutation({
    mutationFn: alpacaAccountsAPI.verify,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['alpacaAccounts'] });
      toast.success(`Verified! Buying power: $${data.data.buying_power}`);
    },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Verification failed'),
  });

  const toggleAlpacaMutation = useMutation({
    mutationFn: alpacaAccountsAPI.toggle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alpacaAccounts'] });
      toast.success('Account status updated');
    },
  });

  const deleteAlpacaMutation = useMutation({
    mutationFn: alpacaAccountsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alpacaAccounts'] });
      toast.success('Alpaca account deleted');
    },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to delete'),
  });

  const handleAlpacaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createAlpacaMutation.mutate({
      ...alpacaFormData,
      user_id: alpacaFormData.user_id ? parseInt(alpacaFormData.user_id) : undefined,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      user_id: formData.user_id ? parseInt(formData.user_id) : undefined,
    };
    createMutation.mutate(submitData);
  };

  const handleOpenLoginUrl = async (account: any) => {
    try {
      const response = await accountsAPI.getLoginUrl(account.id);
      const { login_url } = response.data;

      // Extract API key from URL for debugging
      const urlParams = new URLSearchParams(login_url.split('?')[1]);
      const apiKeyInUrl = urlParams.get('api_key');

      console.log('=== AUTHENTICATION DEBUG ===');
      console.log('Account Name:', account.account_name);
      console.log('Kite User ID:', account.kite_user_id);
      console.log('API Key in Login URL:', apiKeyInUrl);
      console.log('Full Login URL:', login_url);
      console.log('===========================');

      // Show important instructions with API key info
      const userConfirmed = window.confirm(
        `⚠️ AUTHENTICATION DEBUG INFO:\n\n` +
        `Account: ${account.account_name}\n` +
        `Kite User ID: ${account.kite_user_id}\n` +
        `API Key being used: ${apiKeyInUrl}\n\n` +
        `IMPORTANT:\n` +
        `1. Check your browser console for full details\n` +
        `2. Verify this API key matches your developer portal\n` +
        `3. A logout tab will open first\n` +
        `4. Then you'll see the login page\n` +
        `5. Log in with ${account.kite_user_id}'s credentials\n\n` +
        `Click OK to proceed, or Cancel to stop.`
      );

      if (!userConfirmed) {
        return;
      }

      // Open Kite login in a popup
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      // Use unique popup name per account to avoid reusing windows
      // Add timestamp to force new window each time
      const popupName = `KiteLogin_${account.id}_${Date.now()}`;

      // First, open logout URL to clear any existing Zerodha session
      const logoutPopup = window.open(
        'https://kite.zerodha.com/connect/logout',
        popupName,
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Wait longer (4 seconds) for logout to complete, then navigate to login
      setTimeout(() => {
        if (logoutPopup && !logoutPopup.closed) {
          logoutPopup.location.href = login_url;
          toast.success(`Now logging in with ${account.kite_user_id}. Check the popup window.`);
        } else {
          // If popup was blocked or closed, open login directly
          window.open(
            login_url,
            popupName,
            `width=${width},height=${height},left=${left},top=${top}`
          );
        }
      }, 4000);

      setSelectedAccount(account);
      setShowAuthModal(true);
    } catch (error: any) {
      toast.error('Failed to get login URL');
    }
  };

  const handleAuthenticate = () => {
    if (!requestToken) {
      toast.error('Please complete the Kite login first');
      return;
    }
    if (!selectedAccount) {
      toast.error('No account selected');
      return;
    }
    authenticateMutation.mutate({
      id: selectedAccount.id,
      token: requestToken,
    });
  };

  return (
    <div className="space-y-6">
      {/* Tab Switcher */}
      <div className="flex gap-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('kite')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 ${
            activeTab === 'kite'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Kite Accounts
        </button>
        <button
          onClick={() => setActiveTab('alpaca')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 ${
            activeTab === 'alpaca'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Alpaca Accounts
        </button>
        <button
          onClick={() => setActiveTab('upstox')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 ${
            activeTab === 'upstox'
              ? 'border-orange-600 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Upstox Accounts
        </button>
        <button
          onClick={() => setActiveTab('fyers')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 ${
            activeTab === 'fyers'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Fyers Accounts
        </button>
      </div>

      {activeTab === 'kite' ? (
      <>
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Kite Accounts</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Add Account
        </button>
      </div>

      {/* Add Account Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Add New Account</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Account Name
              </label>
              <input
                type="text"
                required
                value={formData.account_name}
                onChange={(e) =>
                  setFormData({ ...formData, account_name: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Link to User (Optional)
              </label>
              <select
                value={formData.user_id}
                onChange={(e) =>
                  setFormData({ ...formData, user_id: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">-- No User (Standalone Account) --</option>
                {users?.map((user: any) => (
                  <option key={user.id} value={user.id}>
                    {user.username} ({user.email})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Link this account to a user for webhook-based trading
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Kite User ID (Zerodha Client ID)
              </label>
              <input
                type="text"
                required
                value={formData.kite_user_id}
                onChange={(e) =>
                  setFormData({ ...formData, kite_user_id: e.target.value })
                }
                placeholder="AB1234"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                API Key
              </label>
              <input
                type="text"
                required
                value={formData.api_key}
                onChange={(e) =>
                  setFormData({ ...formData, api_key: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                API Secret
              </label>
              <input
                type="password"
                required
                value={formData.api_secret}
                onChange={(e) =>
                  setFormData({ ...formData, api_secret: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Create Account
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Accounts List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Account Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Kite User ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Linked User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Authenticated
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {accounts?.map((account: any) => (
              <tr key={account.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {account.account_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {account.kite_user_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {account.user ? (
                    <span className="text-blue-600">
                      {account.user.username}
                    </span>
                  ) : (
                    <span className="text-gray-400">Not linked</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span
                    className={`px-2 py-1 rounded ${
                      account.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {account.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span
                    className={`px-2 py-1 rounded ${
                      account.is_authenticated
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {account.is_authenticated ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => handleOpenLoginUrl(account)}
                    className={account.is_authenticated ? "text-orange-600 hover:text-orange-900" : "text-green-600 hover:text-green-900"}
                    title={account.is_authenticated ? "Re-authenticate" : "Authenticate"}
                  >
                    <Key size={20} />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAccountForHoldings(account);
                      setShowHoldings(true);
                    }}
                    className="text-purple-600 hover:text-purple-900"
                    title="View Holdings"
                  >
                    <Package size={20} />
                  </button>
                  <button
                    onClick={() => toggleMutation.mutate(account.id)}
                    className="text-blue-600 hover:text-blue-900"
                    title="Toggle Active"
                  >
                    {account.is_active ? (
                      <ToggleRight size={20} />
                    ) : (
                      <ToggleLeft size={20} />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Delete this account?')) {
                        deleteMutation.mutate(account.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    title="Delete"
                  >
                    <Trash2 size={20} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Authentication Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Authenticate Account</h2>
            <p className="text-gray-600 mb-4">
              Account: <strong>{selectedAccount?.account_name}</strong>
            </p>

            <div className="mb-4">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <p className="text-sm text-blue-700">
                  <strong>Instructions:</strong>
                </p>
                <ol className="text-sm text-blue-700 list-decimal list-inside mt-2">
                  <li>A popup window should have opened with Kite login</li>
                  <li>Login with your Kite credentials</li>
                  <li>The request token will appear below automatically</li>
                  <li>Click "Authenticate" to complete the process</li>
                </ol>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Request Token
              </label>
              <input
                type="text"
                value={requestToken}
                onChange={(e) => setRequestToken(e.target.value)}
                placeholder="Paste request token here or login via popup"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {requestToken && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ Request token received!
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleAuthenticate}
                disabled={!requestToken || authenticateMutation.isPending}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {authenticateMutation.isPending ? 'Authenticating...' : 'Authenticate'}
              </button>
              <button
                onClick={() => handleOpenLoginUrl(selectedAccount)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                <ExternalLink size={16} />
                Reopen Login
              </button>
              <button
                onClick={() => {
                  setShowAuthModal(false);
                  setRequestToken('');
                  setSelectedAccount(null);
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Holdings Modal */}
      {showHoldings && selectedAccountForHoldings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">
                Holdings - {selectedAccountForHoldings.account_name}
              </h2>
              <button
                onClick={() => syncHoldingsMutation.mutate(selectedAccountForHoldings.id)}
                disabled={syncHoldingsMutation.isPending}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
              >
                {syncHoldingsMutation.isPending ? 'Syncing...' : 'Sync from Zerodha'}
              </button>
            </div>

            {holdingsLoading ? (
              <div className="text-center py-8">Loading holdings...</div>
            ) : holdings?.holdings?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exchange</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg Price</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Last Price</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P&L</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Synced At</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {holdings.holdings.map((holding: any) => (
                      <tr key={holding.id}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {holding.tradingsymbol}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{holding.exchange}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">{holding.quantity}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-500">
                          ${holding.average_price?.toFixed(2) || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-500">
                          ${holding.last_price?.toFixed(2) || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right font-medium ${
                          holding.pnl >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          ${holding.pnl?.toFixed(2) || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(holding.synced_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No holdings synced. Click "Sync from Zerodha" to fetch holdings.
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowHoldings(false);
                  setSelectedAccountForHoldings(null);
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      ) : activeTab === 'alpaca' ? (
      <>
      {/* ─── Alpaca Accounts Tab ─────────────────────────────────────────── */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Alpaca Accounts</h1>
        <button
          onClick={() => setShowAlpacaForm(!showAlpacaForm)}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
        >
          <Plus size={20} />
          Add Alpaca Account
        </button>
      </div>

      {showAlpacaForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Add New Alpaca Account</h2>
          <form onSubmit={handleAlpacaSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Name</label>
              <input
                type="text"
                required
                value={alpacaFormData.account_name}
                onChange={(e) => setAlpacaFormData({ ...alpacaFormData, account_name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Link to User (Optional)</label>
              <select
                value={alpacaFormData.user_id}
                onChange={(e) => setAlpacaFormData({ ...alpacaFormData, user_id: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
              >
                <option value="">-- No User --</option>
                {users?.map((user: any) => (
                  <option key={user.id} value={user.id}>
                    {user.username} ({user.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">API Key</label>
              <input
                type="text"
                required
                value={alpacaFormData.api_key}
                onChange={(e) => setAlpacaFormData({ ...alpacaFormData, api_key: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">API Secret</label>
              <input
                type="password"
                required
                value={alpacaFormData.api_secret}
                onChange={(e) => setAlpacaFormData({ ...alpacaFormData, api_secret: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_paper"
                checked={alpacaFormData.is_paper}
                onChange={(e) => setAlpacaFormData({ ...alpacaFormData, is_paper: e.target.checked })}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <label htmlFor="is_paper" className="text-sm font-medium text-gray-700">
                Paper Trading (recommended for testing)
              </label>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
                Create Account
              </button>
              <button
                type="button"
                onClick={() => setShowAlpacaForm(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Linked User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verified</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {alpacaAccounts?.map((account: any) => (
              <tr key={account.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {account.account_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    account.is_paper ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {account.is_paper ? 'Paper' : 'Live'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {account.user ? (
                    <span className="text-green-600">{account.user.username}</span>
                  ) : (
                    <span className="text-gray-400">Not linked</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded ${
                    account.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {account.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded ${
                    account.is_verified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {account.is_verified ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => verifyAlpacaMutation.mutate(account.id)}
                    className="text-green-600 hover:text-green-900"
                    title="Verify Credentials"
                  >
                    <CheckCircle size={20} />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAlpacaAccount(account);
                      setShowAlpacaPositions(true);
                    }}
                    className="text-purple-600 hover:text-purple-900"
                    title="View Positions"
                  >
                    <Eye size={20} />
                  </button>
                  <button
                    onClick={() => toggleAlpacaMutation.mutate(account.id)}
                    className="text-blue-600 hover:text-blue-900"
                    title="Toggle Active"
                  >
                    {account.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Delete this Alpaca account?')) {
                        deleteAlpacaMutation.mutate(account.id);
                      }
                    }}
                    className="text-red-600 hover:text-red-900"
                    title="Delete"
                  >
                    <Trash2 size={20} />
                  </button>
                </td>
              </tr>
            ))}
            {(!alpacaAccounts || alpacaAccounts.length === 0) && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  No Alpaca accounts yet. Click "Add Alpaca Account" to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Alpaca Positions Modal */}
      {showAlpacaPositions && selectedAlpacaAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-auto">
            <h2 className="text-2xl font-bold mb-4">
              Positions - {selectedAlpacaAccount.account_name}
            </h2>

            {positionsLoading ? (
              <div className="text-center py-8">Loading positions...</div>
            ) : alpacaPositions?.positions?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg Entry</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P&L</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Market Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {alpacaPositions.positions.map((pos: any) => (
                      <tr key={pos.symbol}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{pos.symbol}</td>
                        <td className="px-4 py-3 text-sm text-right">{pos.qty}</td>
                        <td className="px-4 py-3 text-sm text-right">${parseFloat(pos.avg_entry_price).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right">${parseFloat(pos.current_price).toFixed(2)}</td>
                        <td className={`px-4 py-3 text-sm text-right font-medium ${
                          parseFloat(pos.unrealized_pl) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          ${parseFloat(pos.unrealized_pl).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">${parseFloat(pos.market_value).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No open positions.</div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowAlpacaPositions(false);
                  setSelectedAlpacaAccount(null);
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      ) : activeTab === 'upstox' ? (
      <UpstoxTab users={users} queryClient={queryClient} />
      ) : (
      <FyersTab users={users} queryClient={queryClient} />
      )}
    </div>
  );
};

// ─── Upstox Tab Component ──────────────────────────────────────────────────────
const UpstoxTab: React.FC<{ users: any; queryClient: any }> = ({ users, queryClient }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ account_name: '', api_key: '', api_secret: '', user_id: '' });
  const [holdingsAccount, setHoldingsAccount] = useState<any>(null);
  const [holdings, setHoldings] = useState<any[]>([]);
  const [holdingsLoading, setHoldingsLoading] = useState(false);

  const { data: accounts } = useQuery({
    queryKey: ['upstoxAccounts'],
    queryFn: () => upstoxAccountsAPI.list().then((res) => res.data),
  });

  const createMutation = useMutation({
    mutationFn: upstoxAccountsAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upstoxAccounts'] });
      toast.success('Upstox account created');
      setShowForm(false);
      setFormData({ account_name: '', api_key: '', api_secret: '', user_id: '' });
    },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to create account'),
  });

  const toggleMutation = useMutation({
    mutationFn: upstoxAccountsAPI.toggle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upstoxAccounts'] });
      toast.success('Account status updated');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: upstoxAccountsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upstoxAccounts'] });
      toast.success('Account deleted');
    },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to delete'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      ...formData,
      user_id: formData.user_id ? parseInt(formData.user_id) : undefined,
    });
  };

  const handleLogin = async (account: any) => {
    try {
      const redirectUri = window.location.origin + '/api/v1/accounts/callback';
      const response = await upstoxAccountsAPI.getLoginUrl(account.id, redirectUri);
      window.open(response.data.login_url, '_blank', 'width=600,height=700');
      toast.success('Complete login in the popup window.');
    } catch {
      toast.error('Failed to get login URL');
    }
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Upstox Accounts</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
        >
          <Plus size={20} />
          Add Upstox Account
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Add New Upstox Account</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Name</label>
              <input type="text" required value={formData.account_name}
                onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Link to User (Optional)</label>
              <select value={formData.user_id}
                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500">
                <option value="">-- No User --</option>
                {users?.map((user: any) => (
                  <option key={user.id} value={user.id}>{user.username} ({user.email})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">API Key</label>
              <input type="text" required value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">API Secret</label>
              <input type="password" required value={formData.api_secret}
                onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500" />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700">Create Account</button>
              <button type="button" onClick={() => setShowForm(false)} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Linked User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Authenticated</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {accounts?.map((account: any) => (
              <tr key={account.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{account.account_name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {account.user ? <span className="text-orange-600">{account.user.username}</span> : <span className="text-gray-400">Not linked</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded ${account.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {account.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded ${account.is_authenticated ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                    {account.is_authenticated ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button onClick={() => handleLogin(account)} className="text-green-600 hover:text-green-900" title="Authenticate"><Key size={20} /></button>
                  <button onClick={async () => { if (account.is_authenticated) { setHoldingsLoading(true); setHoldingsAccount(account); try { const r = await upstoxAccountsAPI.getHoldings(account.id); setHoldings(r.data.holdings || []); } catch { toast.error('Failed to fetch holdings'); } setHoldingsLoading(false); } else { toast.error('Authenticate first'); }}}
                    className="text-purple-600 hover:text-purple-900" title="View Holdings"><Package size={20} /></button>
                  <button onClick={() => toggleMutation.mutate(account.id)} className="text-blue-600 hover:text-blue-900" title="Toggle Active">
                    {account.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                  </button>
                  <button onClick={() => { if (window.confirm('Delete this account?')) deleteMutation.mutate(account.id); }}
                    className="text-red-600 hover:text-red-900" title="Delete"><Trash2 size={20} /></button>
                </td>
              </tr>
            ))}
            {(!accounts || accounts.length === 0) && (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-gray-500">No Upstox accounts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Holdings Modal */}
      {holdingsAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Holdings - {holdingsAccount.account_name}</h2>
              <button onClick={() => setHoldingsAccount(null)} className="text-gray-500 hover:text-gray-700 text-xl font-bold">×</button>
            </div>
            {holdingsLoading ? (
              <div className="text-center py-8">Loading holdings...</div>
            ) : holdings.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg Price</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">LTP</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P&L</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {holdings.map((h: any, i: number) => (
                    <tr key={i}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{h.tradingsymbol || h.trading_symbol || h.symbol}</td>
                      <td className="px-4 py-3 text-sm text-right">{h.quantity}</td>
                      <td className="px-4 py-3 text-sm text-right">{h.average_price?.toFixed(2) || '-'}</td>
                      <td className="px-4 py-3 text-sm text-right">{h.last_price?.toFixed(2) || h.ltp?.toFixed(2) || '-'}</td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${(h.pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {h.pnl?.toFixed(2) || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-gray-500">No holdings found.</div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ─── Fyers Tab Component ───────────────────────────────────────────────────────
const FyersTab: React.FC<{ users: any; queryClient: any }> = ({ users, queryClient }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ account_name: '', client_id: '', secret_key: '', user_id: '' });
  const [holdingsAccount, setHoldingsAccount] = useState<any>(null);
  const [holdings, setHoldings] = useState<any[]>([]);
  const [holdingsLoading, setHoldingsLoading] = useState(false);

  const { data: accounts } = useQuery({
    queryKey: ['fyersAccounts'],
    queryFn: () => fyersAccountsAPI.list().then((res) => res.data),
  });

  const createMutation = useMutation({
    mutationFn: fyersAccountsAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fyersAccounts'] });
      toast.success('Fyers account created');
      setShowForm(false);
      setFormData({ account_name: '', client_id: '', secret_key: '', user_id: '' });
    },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to create account'),
  });

  const toggleMutation = useMutation({
    mutationFn: fyersAccountsAPI.toggle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fyersAccounts'] });
      toast.success('Account status updated');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: fyersAccountsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fyersAccounts'] });
      toast.success('Account deleted');
    },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to delete'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      ...formData,
      user_id: formData.user_id ? parseInt(formData.user_id) : undefined,
    });
  };

  const handleLogin = async (account: any) => {
    try {
      const redirectUri = window.location.origin + '/api/v1/accounts/callback';
      const response = await fyersAccountsAPI.getLoginUrl(account.id, redirectUri);
      window.open(response.data.login_url, '_blank', 'width=600,height=700');
      toast.success('Complete login in the popup window.');
    } catch {
      toast.error('Failed to get login URL');
    }
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Fyers Accounts</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
        >
          <Plus size={20} />
          Add Fyers Account
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Add New Fyers Account</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Name</label>
              <input type="text" required value={formData.account_name}
                onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Link to User (Optional)</label>
              <select value={formData.user_id}
                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500">
                <option value="">-- No User --</option>
                {users?.map((user: any) => (
                  <option key={user.id} value={user.id}>{user.username} ({user.email})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Client ID (App ID)</label>
              <input type="text" required value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                placeholder="XXXXXXXXXXX-100"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Secret Key</label>
              <input type="password" required value={formData.secret_key}
                onChange={(e) => setFormData({ ...formData, secret_key: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500" />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">Create Account</button>
              <button type="button" onClick={() => setShowForm(false)} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Linked User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Authenticated</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {accounts?.map((account: any) => (
              <tr key={account.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{account.account_name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {account.user ? <span className="text-purple-600">{account.user.username}</span> : <span className="text-gray-400">Not linked</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded ${account.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {account.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded ${account.is_authenticated ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                    {account.is_authenticated ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button onClick={() => handleLogin(account)} className="text-green-600 hover:text-green-900" title="Authenticate"><Key size={20} /></button>
                  <button onClick={async () => { if (account.is_authenticated) { setHoldingsLoading(true); setHoldingsAccount(account); try { const r = await fyersAccountsAPI.getHoldings(account.id); setHoldings(r.data.holdings || []); } catch { toast.error('Failed to fetch holdings'); } setHoldingsLoading(false); } else { toast.error('Authenticate first'); }}}
                    className="text-purple-600 hover:text-purple-900" title="View Holdings"><Package size={20} /></button>
                  <button onClick={() => toggleMutation.mutate(account.id)} className="text-blue-600 hover:text-blue-900" title="Toggle Active">
                    {account.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                  </button>
                  <button onClick={() => { if (window.confirm('Delete this account?')) deleteMutation.mutate(account.id); }}
                    className="text-red-600 hover:text-red-900" title="Delete"><Trash2 size={20} /></button>
                </td>
              </tr>
            ))}
            {(!accounts || accounts.length === 0) && (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-gray-500">No Fyers accounts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Holdings Modal */}
      {holdingsAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Holdings - {holdingsAccount.account_name}</h2>
              <button onClick={() => setHoldingsAccount(null)} className="text-gray-500 hover:text-gray-700 text-xl font-bold">×</button>
            </div>
            {holdingsLoading ? (
              <div className="text-center py-8">Loading holdings...</div>
            ) : holdings.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost Price</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">LTP</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P&L</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Market Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {holdings.map((h: any, i: number) => (
                    <tr key={i}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{h.symbol?.split(':')[1]?.replace('-EQ','') || h.symbol}</td>
                      <td className="px-4 py-3 text-sm text-right">{h.quantity}</td>
                      <td className="px-4 py-3 text-sm text-right">{h.costPrice?.toFixed(2) || '-'}</td>
                      <td className="px-4 py-3 text-sm text-right">{h.ltp?.toFixed(2) || '-'}</td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${(h.pl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {h.pl?.toFixed(2) || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">{h.marketVal?.toFixed(2) || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-gray-500">No holdings found.</div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Accounts;
